import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AuthLegalFooter, LegalModal } from '../components/AuthLegalFooter';
import WasslaLogo from '../components/WasslaLogo';
import { isNativeApp } from '../utils/nativeGoogleAuth';
import { useAuth } from '../contexts/AuthContext';
import { useAppVersion } from '../version';
import './Welcome.css';

function Welcome() {
  const { t, i18n } = useTranslation();
  const { label: appVersionLabel } = useAppVersion();
  const navigate = useNavigate();
  const { beginGoogleOAuth, user, loading, oauthInProgress } = useAuth();
  
  // Legal modal state
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalModalType, setLegalModalType] = useState('terms');

  // Structural guards — Welcome can NEVER paint while a Google OAuth
  // round-trip is pending/transitioning (overlay owns the screen), while auth
  // is still hydrating, or once a user is authenticated (redirect to Home).
  if (oauthInProgress) return null;
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleGoogleSignup = async () => {
    if (isNativeApp()) {
      // Android: Google blocks OAuth in WebViews — run the same Emergent
      // managed flow in the system browser; deep link returns the session_id.
      // beginGoogleOAuth raises the full-screen "Signing you in…" state BEFORE
      // leaving the app so this page never flashes during the OAuth return.
      try {
        await beginGoogleOAuth();
      } catch (e) {
        toast.error(i18n.language === 'ar'
          ? 'تعذّر فتح تسجيل الدخول عبر Google. تحقق من اتصالك بالإنترنت.'
          : 'Could not open Google sign-in. Check your internet connection.');
      }
      return;
    }
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Wassla-branded Google OAuth (own client) — backend drives the flow
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `${window.location.origin}/api/auth/google/start?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="welcome-page">
      <div className="welcome-container">
        <div className="welcome-content">
          {/* New Wassla Logo */}
          <div className="welcome-logo" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '1.5rem'
          }}>
            <WasslaLogo size="auth" />
          </div>
          
          <h2 className="welcome-title">{t('welcomePageTitle')}</h2>
          <p className="welcome-subtitle">{t('welcomePageSubtitle')}</p>

          <div className="welcome-buttons">
            <button
              onClick={handleGoogleSignup}
              className="btn btn-google"
              data-testid="google-signup-button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '0.75rem' }}>
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('signUpWithGoogle')}
            </button>

            <button
              onClick={() => navigate('/auth?mode=signup')}
              className="btn btn-email"
              data-testid="email-signup-button"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ marginRight: '0.75rem' }}>
                <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {t('signUpWithEmail')}
            </button>
          </div>

          <div className="welcome-signin">
            <p>
              {t('alreadyAccount')}
              <button
                onClick={() => navigate('/auth?mode=login')}
                className="signin-link"
                data-testid="signin-link"
              >
                {t('signInLink')}
              </button>
            </p>
          </div>

          {/* Legal Footer - Privacy Policy & Terms of Use */}
          <AuthLegalFooter 
            locale={i18n.language}
            onOpenPrivacy={() => {
              setLegalModalType('privacy');
              setLegalModalOpen(true);
            }}
            onOpenTerms={() => {
              setLegalModalType('terms');
              setLegalModalOpen(true);
            }}
          />

          {/* Build marker — proves which bundle is actually running on-device */}
          <p
            data-testid="welcome-version-label"
            style={{ marginTop: 12, textAlign: 'center', fontSize: 11, opacity: 0.45 }}
          >
            {appVersionLabel}
          </p>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal
        open={legalModalOpen}
        onClose={() => setLegalModalOpen(false)}
        type={legalModalType}
        locale={i18n.language}
      />
    </div>
  );
}

export default Welcome;
